import { createEvent, createStore, sample } from 'effector'
import {
  NotificationPayload,
  showNotification,
} from 'src/features/notifications'
import { fx } from 'src/libs'
import { blockGatewayUrl } from '../const'
import { BlockTarget, GatewayBlockBody, GatewayBlockRequest } from './types'
import { toBlockRequestBody } from './utils'

export const openBlockModal = createEvent<BlockTarget>()
export const closeBlockModal = createEvent()
export const submitGatewayBlock = createEvent<GatewayBlockRequest>()

// The only thing the rest of the widget subscribes to: a status was changed
// on the backend and every view of it is now stale.
export const gatewayBlockChanged = createEvent()

export const blockGatewayFx = fx<GatewayBlockBody, void>({
  method: 'POST',
  url: blockGatewayUrl,
})

export const $blockTarget = createStore<BlockTarget | null>(null)
  .on(openBlockModal, (_, gateway) => gateway)
  .reset(closeBlockModal)

// Which row is in flight — the list uses it to show a spinner on that button
// and to disable the others.
export const $pendingGatewayId = createStore<number | null>(null)
  .on(submitGatewayBlock, (_, { gatewayId }) => gatewayId)
  .reset(blockGatewayFx.finally)

// Kept for the success message, which needs to know what was asked for after
// the request resolves.
const $lastIsBlocked = createStore(false).on(
  submitGatewayBlock,
  (_, { isBlocked }) => isBlocked,
)

sample({
  clock: submitGatewayBlock,
  fn: (request) => ({ body: toBlockRequestBody(request) }),
  target: blockGatewayFx,
})

sample({
  clock: blockGatewayFx.done,
  target: gatewayBlockChanged,
})

// The modal stays open on failure so the typed reason is not lost.
sample({
  clock: gatewayBlockChanged,
  target: closeBlockModal,
})

sample({
  clock: blockGatewayFx.done,
  source: $lastIsBlocked,
  fn: (isBlocked): NotificationPayload => ({
    type: 'success',
    description: isBlocked ? 'Gateway blocked' : 'Gateway unblocked',
  }),
  target: showNotification,
})

sample({
  clock: blockGatewayFx.failData,
  fn: (error): NotificationPayload => ({
    type: 'error',
    description: error?.body?.message ?? 'Request failed',
  }),
  target: showNotification,
})
